import type { JSX } from 'react';
import { NavbarDropdownTextHeader } from './NavbarDropdown.tsx';
import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import { OperatorAppRoutes } from '../../app/config/OperatorRoutingConfig.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { css } from '@emotion/css';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { useMatches } from 'react-router';
import { Icon16, Icon24 } from '../../ui/icons/Icon.tsx';
import { chevronBreadcrumb } from 'dg-web-shared/ui/icons/Icons16.tsx';
import { menu } from 'dg-web-shared/ui/icons/Icons24.tsx';
import { OffstreetTransactionsTitle } from '../../offstreet-transactions/OperatorOffstreetTransactionsRoute.tsx';

import parkingPortalLogo from '../../../assets/parkingportal_logo_white.svg';

const MenuIcon = () => <Icon24 icon={menu} />;

const ParkingPortalLogo = () => (
    <img
        src={parkingPortalLogo}
        className={css({
            width: 136,
            height: 48,
        })}
    />
);

function Chevron() {
    return (
        <div
            className={css({
                height: 24,
                width: 24,
                textAlign: 'center',
                paddingTop: 4,
                lineHeight: '20px',
            })}
        >
            <Icon16 icon={chevronBreadcrumb} />
        </div>
    );
}

type RouteMatch = ReturnType<typeof useMatches>[0];

type MatchWithTitle = RouteMatch & {
    handle: {
        title: JSX.Element;
    };
};

function isTitleMatch(match: RouteMatch): match is MatchWithTitle {
    return (
        Boolean(match.handle) && Boolean((match as MatchWithTitle).handle.title)
    );
}

export function useIsMobileRoute() {
    const matches = useMatches();
    return matches.some(match =>
        Boolean(
            match.handle &&
            (
                match.handle as {
                    isMobile: boolean;
                }
            ).isMobile,
        ),
    );
}

export type MatchWithRouteName = RouteMatch & {
    handle: {
        routeName: OperatorAppRoutes;
    };
};

export function isRouteNameMatch(
    match: RouteMatch,
): match is MatchWithRouteName {
    return (
        Boolean(match.handle) &&
        Boolean((match as MatchWithRouteName).handle.routeName)
    );
}

export function BreadCrumb() {
    const { authentication } = useOperatorContext();
    const { operatorData } = useOperatorContext();
    const crumbsWithNavText = useMatches().filter(crumb =>
        hasNavigationText(crumb, operatorData),
    );

    return (
        <div className={css({ display: 'flex' })}>
            <div
                className={css({
                    padding: 12,
                    height: 48,
                    display: 'flex',
                })}
            >
                <MenuIcon />
            </div>
            <ParkingPortalLogo />
            {authentication.authorized && (
                <div
                    className={css({
                        display: 'flex',
                        alignItems: 'center',
                        height: 48,
                    })}
                >
                    {crumbsWithNavText.map((crumb, index) => (
                        <NavbarDropdownText
                            key={crumb.id}
                            crumb={crumb}
                            operatorData={operatorData}
                            lastElement={index < crumbsWithNavText.length - 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function NavbarDropdownText({
    crumb,
    operatorData,
    lastElement,
}: {
    crumb: RouteMatch;
    operatorData: OperatorDataState.OperatorData;
    lastElement: boolean;
}) {
    if (isTitleMatch(crumb)) {
        return (
            <>
                <NavbarDropdownTextHeader>
                    {crumb.handle.title}
                </NavbarDropdownTextHeader>
                {lastElement && <Chevron />}
            </>
        );
    }

    if (isRouteNameMatch(crumb)) {
        return (
            <>
                <NavbarDropdownTextHeader>
                    {getNavigationText(crumb.handle.routeName, operatorData)}
                </NavbarDropdownTextHeader>
                {lastElement && <Chevron />}
            </>
        );
    }

    return null;
}

function hasNavigationText(
    crumb: RouteMatch,
    operatorData: OperatorDataState.OperatorData,
): boolean {
    if (isTitleMatch(crumb)) {
        return true;
    }

    if (isRouteNameMatch(crumb)) {
        return getNavigationText(crumb.handle.routeName, operatorData) !== null;
    }

    return false;
}

export function getNavigationText(
    route: OperatorAppRoutes,
    operatorData?: OperatorDataState.OperatorData,
): JSX.Element | null {
    switch (route) {
        case OperatorAppRoutes.Home:
            return (
                <Localized
                    de="Betreiberkonto"
                    fr="Compte exploitant"
                    it="Conto gestore"
                    en="Operator account"
                />
            );
        case OperatorAppRoutes.Zones:
            return <Localized de="Zonen" fr="Zones" it="Zone" en="Zones" />;
        case OperatorAppRoutes.ZonesDetail:
        case OperatorAppRoutes.ZonesList:
            return <Localized de="Zonen" fr="Zones" it="Zone" en="Zones" />;
        case OperatorAppRoutes.Parkings:
        case OperatorAppRoutes.ParkingsList:
            return (
                <Localized
                    de="Parkings"
                    fr="Parkings"
                    it="Parcheggi"
                    en="Parkings"
                />
            );
        case OperatorAppRoutes.CloudConnectorPeripheries:
            return (
                <Localized
                    de="Geräte"
                    fr="Appareils"
                    it="Apparecchi"
                    en="Devices"
                />
            );
        case OperatorAppRoutes.CloudConnectorCounterOverview:
            return (
                <Localized
                    de="Zähler"
                    fr="Compteurs"
                    it="Contatori"
                    en="Counters"
                />
            );
        case OperatorAppRoutes.CloudConnectorRecodeStatements:
        case OperatorAppRoutes.CloudConnectorAutoRecode:
        case OperatorAppRoutes.CloudConnectorRecodeQrCodes:
            return (
                <Localized
                    de="Rabattauszug"
                    fr="Relevé des rabais"
                    it="Estratto ribassi"
                    en="Discounts statament"
                />
            );
        case OperatorAppRoutes.PayStationCollections:
        case OperatorAppRoutes.PayStationStatement:
            return (
                <Localized
                    de="Kassenauszug"
                    fr="Relevé de caisse"
                    it="Estratto cassa"
                    en="Pay station statement"
                />
            );
        case OperatorAppRoutes.PeripheryHistory:
            return (
                <Localized
                    de="Geräte-History"
                    fr="Historique des appareils"
                    it="Storico apparecchi"
                    en="Devices History"
                />
            );
        case OperatorAppRoutes.ClearancesPermitsList:
            return operatorData?.licensePlatePermitSettings.whitelistActive ? (
                <Localized
                    de="Bewilligungen & Freigaben"
                    fr="Autorisations & approbations"
                    it="Autorizzazioni & approvazioni"
                    en="Authorizations & Approvals"
                />
            ) : (
                <Localized
                    de="Bewilligungen"
                    fr="Autorisations"
                    it="Autorizzazioni"
                    en="Authorizations"
                />
            );
        case OperatorAppRoutes.ClearanceRequests:
            return (
                <Localized
                    de="Anträge"
                    fr="Demandes"
                    it="Richieste"
                    en="Requests"
                />
            );
        case OperatorAppRoutes.Logins:
            return (
                <Localized
                    de="Zugriffsrechte"
                    fr="Droits d'accès"
                    it="Diritti d'accesso"
                    en="Access Rights"
                />
            );
        case OperatorAppRoutes.ActivityStatement:
            return (
                <Localized
                    de="Kontoauszug"
                    fr="Relevés de compte"
                    it="Estratto conto"
                    en="Account Balance"
                />
            );
        case OperatorAppRoutes.CustomerAccounts:
            return (
                <Localized
                    de="Benutzerkonti"
                    fr="Comptes utilisateur"
                    it="Conti utente"
                    en="Customer Accounts"
                />
            );
        case OperatorAppRoutes.SpecialReports:
            return (
                <Localized
                    de="Spezialreporte"
                    fr="Rapports spéciaux"
                    it="Liste personalizzate"
                    en="Special Reports"
                />
            );
        case OperatorAppRoutes.PermitTypes:
            return (
                <Localized
                    de="Bewilligungsarten"
                    fr="Types d'autorisations"
                    it="Tipi d'autorizzazione"
                    en="Permit Types"
                />
            );
        case OperatorAppRoutes.ParkingMeters:
            return (
                <Localized
                    de="TOMeco-Parkuhren"
                    fr="Horodateurs TOMeco"
                    it="Parchimetri TOMeco"
                    en="TOMeco parking meters"
                />
            );
        case OperatorAppRoutes.Tariffs:
            return (
                <Localized de="Tarife" fr="Tarifs" it="Tariffe" en="Tariffs" />
            );
        case OperatorAppRoutes.QuotasOverview:
            return (
                <Localized
                    de="Kontingente"
                    fr="Contingents"
                    it="Contingenti"
                    en="Quotas"
                />
            );
        case OperatorAppRoutes.UsbSticks:
            return (
                <Localized
                    de="USB Sticks"
                    fr="Clés USB"
                    it="Chiavette USB"
                    en="USB Sticks"
                />
            );
        case OperatorAppRoutes.EnforcementLog:
            return (
                <Localized
                    de="Kontroll-History"
                    fr="Historique des contrôles"
                    it="Storico controlli"
                    en="Enforcement History"
                />
            );
        case OperatorAppRoutes.Parkmonitor:
            return (
                <Localized
                    de="Parkmonitor"
                    fr="Parkmonitor"
                    it="Parkmonitor"
                    en="Parkmonitor"
                />
            );
        case OperatorAppRoutes.MiOffice:
            return (
                <Localized
                    de="MiOffice"
                    fr="MiOffice"
                    it="MiOffice"
                    en="MiOffice"
                />
            );
        case OperatorAppRoutes.ParkingaboUsers:
            return (
                <Localized
                    de="Benutzerkonti"
                    fr="Comptes utilisateur"
                    it="Conti utente"
                    en="User accounts"
                />
            );
        case OperatorAppRoutes.ParkingaboProducts:
            return (
                <Localized
                    de="Produkte"
                    fr="Produits"
                    it="Prodotti"
                    en="Products"
                />
            );
        case OperatorAppRoutes.ParkingaboRoles:
            return <Localized de="Rollen" fr="Rôles" it="Ruoli" en="Roles" />;
        case OperatorAppRoutes.ParkingaboProductTypes:
            return (
                <Localized
                    de="Produkttypen"
                    fr="Types de produits"
                    it="Tipi di prodotto"
                    en="Product types"
                />
            );
        case OperatorAppRoutes.StatisticsCheckins:
            return (
                <Localized
                    de="Statistiken Parkvorgänge"
                    fr="Statistiques procédures"
                    it="Statistiche procedure"
                    en="Procedure statistics"
                />
            );
        case OperatorAppRoutes.StatisticsPermits:
            return (
                <Localized
                    de="Statistiken Bewilligungen"
                    fr="Statistiques autorisations"
                    it="Statistiche autorizzazioni"
                    en="Permit statistics"
                />
            );
        case OperatorAppRoutes.OnstreetTransactions:
        case OperatorAppRoutes.OnstreetTransactionsContractDetail:
            if (
                operatorData?.hasOnstreetTransactions &&
                operatorData.hasTaxomexModule
            ) {
                return (
                    <Localized
                        de="Parkvorgänge & TOMeco"
                        fr="Procédures & TOMeco"
                        it="Procedure & TOMeco"
                        en="Procedures & TOMeco"
                    />
                );
            } else if (operatorData?.hasTaxomexModule) {
                return (
                    <Localized
                        de="TOMeco-Transaktionen"
                        fr="Transactions TOMeco"
                        it="Transazioni TOMeco"
                        en="TOMeco transactions"
                    />
                );
            } else {
                return (
                    <Localized
                        de="Parkvorgänge"
                        fr="Procédures"
                        it="Procedure"
                        en="Procedures"
                    />
                );
            }
        case OperatorAppRoutes.OffstreetTransactions:
            return operatorData?.existingOffstreetTransactionsType ? (
                <OffstreetTransactionsTitle
                    offstreetTransactionsType={
                        operatorData.existingOffstreetTransactionsType
                    }
                />
            ) : null;
        case OperatorAppRoutes.OffstreetGuestProducts:
        case OperatorAppRoutes.OffstreetGuestDayPassOverview:
            return (
                <Localized
                    de="Gastprodukte"
                    fr="Produits hôtes"
                    it="Prodotti ospiti"
                    en="Guest products"
                />
            );
        case OperatorAppRoutes.OffstreetEvents:
        case OperatorAppRoutes.OffstreetEventCards:
            return (
                <Localized
                    de="Veranstaltungskarten"
                    fr="Cartes manifestation"
                    it="Carte evento"
                    en="Event cards"
                />
            );
        case OperatorAppRoutes.Downloads:
            return (
                <Localized
                    de="Downloads"
                    fr="Téléchargements"
                    it="Downloads"
                    en="Downloads"
                />
            );
        case OperatorAppRoutes.LoginProfile:
            return (
                <Localized de="Profil" fr="Profil" it="Profilo" en="Profile" />
            );
        case OperatorAppRoutes.Password:
        case OperatorAppRoutes.LoginDetail:
        case OperatorAppRoutes.LoginCreate:
        case OperatorAppRoutes.TariffDetails:
        case OperatorAppRoutes.ParkingsDetail:
        case OperatorAppRoutes.OffstreetTransactionsTicketDetail:
        case OperatorAppRoutes.OffstreetTransactionsTicketLostDetail:
        case OperatorAppRoutes.OffstreetTransactionsEntryWithoutExitDetail:
        case OperatorAppRoutes.OffstreetTransactionsContractDetail:
        case OperatorAppRoutes.OffstreetEventsAdd:
        case OperatorAppRoutes.OffstreetEventEdit:
        case OperatorAppRoutes.OffstreetEventCardsEditStack:
        case OperatorAppRoutes.OffstreetEventCardsAddStack:
        case OperatorAppRoutes.OffstreetGuestReservationAdd:
        case OperatorAppRoutes.OffstreetGuestReservationAddSuccess:
        case OperatorAppRoutes.OffstreetGuestDayPassAdd:
        case OperatorAppRoutes.ParkingaboProductsDetail:
        case OperatorAppRoutes.ParkingaboUsersAdd:
        case OperatorAppRoutes.ParkingaboUsersInvite:
        case OperatorAppRoutes.ParkingaboUsersDetail:
        case OperatorAppRoutes.ParkingaboUsersProducts:
        case OperatorAppRoutes.ParkingaboUsersProductsAdd:
        case OperatorAppRoutes.ParkingaboUsersProductsDetail:
        case OperatorAppRoutes.ParkingaboUsersTransactions:
        case OperatorAppRoutes.ParkingaboUsersVehicles:
        case OperatorAppRoutes.ParkingaboUsersVehiclesAdd:
        case OperatorAppRoutes.ParkingaboUsersVehiclesDetail:
        case OperatorAppRoutes.ParkingaboUsersAccountStatement:
        case OperatorAppRoutes.ParkingaboProductTypesDetail:
        case OperatorAppRoutes.QuotasDetail:
        case OperatorAppRoutes.PermitTypesDetail:
        case OperatorAppRoutes.QuotasReduction:
        case OperatorAppRoutes.CloudConnectorPeripheriesFullscreen:
        case OperatorAppRoutes.MobileHomePage:
        case OperatorAppRoutes.MobileCloudConnectorPeripheries:
        case OperatorAppRoutes.MobileCloudConnectorSettings:
        case OperatorAppRoutes.MobileCloudCounterOverview:
        case OperatorAppRoutes.MobileCloudCounterDetail:
        case OperatorAppRoutes.CloudConnectorPeripheriesDetail:
        case OperatorAppRoutes.CloudConnectorPayStationManualCorrection:
        case OperatorAppRoutes.CloudConnectorPeripheryDetailPresence:
        case OperatorAppRoutes.CloudConnectorPeripheryDetailStatus:
        case OperatorAppRoutes.CloudConnectorCounterDetail:
        case OperatorAppRoutes.CloudConnectorGenerateGuestPermit:
        case OperatorAppRoutes.CloudConnectorGenerateGuestPermitSuccess:
        case OperatorAppRoutes.CloudConnectorGenerateGuestPermitError:
        case OperatorAppRoutes.CloudConnectorRecode:
        case OperatorAppRoutes.CloudConnectorRecodeSuccessResponse:
        case OperatorAppRoutes.CloudConnectorRecodeErrorResponse:
        case OperatorAppRoutes.CloudConnectorRecodeLicsenPlateSerach:
        case OperatorAppRoutes.CloudConnectorRecodeEntryWithoutExit:
        case OperatorAppRoutes.CloudConnectorRecodeEntryWithoutExitCondition:
        case OperatorAppRoutes.CloudConnectorRecodeQrCodesAdd:
        case OperatorAppRoutes.CloudConnectorSmartCall:
        case OperatorAppRoutes.CloudConnectorSmartCallProfiles:
        case OperatorAppRoutes.CloudConnectorSmartCallProfileAdd:
        case OperatorAppRoutes.CloudConnectorSmartCallProfileDetails:
        case OperatorAppRoutes.CloudConnectorSmartCallWeeklyPlan:
        case OperatorAppRoutes.CloudConnectorSmartCallIndividualDates:
        case OperatorAppRoutes.CloudConnectorAutoRecodeCreate:
        case OperatorAppRoutes.CloudConnectorAutoRecodeEdit:
        case OperatorAppRoutes.Privacy:
        case OperatorAppRoutes.ConfirmOtp:
        case OperatorAppRoutes.EnforcementLogDetail:
            return null;
    }
}
